import { useEffect, useState } from "react";
import { Users, Building2, Briefcase, FileText, Home, Plus, Eye, Check, X } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner, EmptyState, LicenceBadge } from "@/components/shared";
import { toast } from "sonner";
import { AdminCommunity } from "@/components/AdminCommunity";
import { AdminData } from "@/components/AdminData";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-[#E3E8E8] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="w-10 h-10 rounded-xl bg-[#E8F5F1] flex items-center justify-center mb-3"><Icon size={18} className="text-[#007F83]" /></div>
    <p className="font-display font-extrabold text-3xl text-[#0B1733]">{value ?? "—"}</p>
    <p className="text-sm text-[#4A5568]">{label}</p>
  </div>
);

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [cats, setCats] = useState([]);
  const [svc, setSvc] = useState({ name: "", short_description: "", parent_category: "", icon: "wrench", featured: false });
  const [promos, setPromos] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const in60 = new Date(Date.now() + 60 * 864e5).toISOString().slice(0, 10);
  const [promo, setPromo] = useState({ company_id: "", placement: "homepage_featured", category_slug: "", location: "", headline: "", priority: 10, start_date: today, end_date: in60, status: "active", free_launch_reason: "" });

  const loadCompanies = () => api.get("/admin/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
  const loadPromos = () => api.get("/admin/promotions").then((r) => setPromos(r.data)).catch(() => setPromos([]));
  const [disputes, setDisputes] = useState([]);
  const loadDisputes = () => api.get("/admin/disputes").then((r) => setDisputes(r.data)).catch(() => setDisputes([]));
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    loadCompanies();
    loadPromos();
    loadDisputes();
  }, []);

  const resolveDispute = async (jobId, resolution) => {
    try { await api.post(`/admin/jobs/${jobId}/resolve`, { resolution }); toast.success(`Resolved: ${resolution}`); loadDisputes(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createPromo = async () => {
    if (!promo.company_id) { toast.error("Choose a company"); return; }
    try {
      await api.post("/admin/promotions", { ...promo, priority: Number(promo.priority), category_slug: promo.category_slug || null, location: promo.location || null });
      toast.success("Promotion created — now live if within dates");
      loadPromos();
      setPromo({ ...promo, company_id: "", headline: "", free_launch_reason: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setPromoStatus = async (id, status) => {
    try { await api.post(`/admin/promotions/${id}/status`, { status }); loadPromos(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const review = async (id, decision) => {
    try { await api.post(`/admin/companies/${id}/review`, { decision }); toast.success(`Company ${decision}`); loadCompanies(); api.get("/admin/stats").then((r) => setStats(r.data)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewLicence = (id) => {
    const token = localStorage.getItem("propora_token");
    window.open(`${API}/companies/${id}/licence-document?auth=${token}`, "_blank");
  };

  const createService = async () => {
    if (!svc.name || !svc.short_description) { toast.error("Name and description required"); return; }
    try { await api.post("/admin/services", { ...svc, intake_fields: [] }); toast.success("Service created — it is now live publicly"); api.get("/categories").then((r) => setCats(r.data)); setSvc({ name: "", short_description: "", parent_category: "", icon: "wrench", featured: false }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="propora-container py-10" data-testid="admin-page">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#007F83] mb-2">Admin workspace</p>
      <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">Platform overview</h1>

      {!stats ? <Spinner /> : (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <StatCard icon={Users} label="Users" value={stats.users} />
          <StatCard icon={Building2} label="Companies" value={stats.companies} />
          <StatCard icon={FileText} label="Pending review" value={stats.companies_pending} />
          <StatCard icon={Check} label="Approved" value={stats.companies_approved} />
          <StatCard icon={Briefcase} label="Jobs" value={stats.jobs} />
          <StatCard icon={Home} label="Properties" value={stats.properties} />
          <StatCard icon={Plus} label="Services" value={stats.services} />
        </div>
      )}

      <Tabs defaultValue="licences" className="mt-10">
        <TabsList className="flex-wrap h-auto justify-start">
          <TabsTrigger value="licences" data-testid="admin-tab-licences">Licence review</TabsTrigger>
          <TabsTrigger value="services" data-testid="admin-tab-services">Services</TabsTrigger>
          <TabsTrigger value="promotions" data-testid="admin-tab-promotions">Promotions</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes">Disputes{disputes.length > 0 ? ` (${disputes.length})` : ""}</TabsTrigger>
          <TabsTrigger value="community" data-testid="admin-tab-community">Community</TabsTrigger>
          <TabsTrigger value="data" data-testid="admin-tab-data">Data cleanup</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="mt-6">
          <AdminData companies={companies} onChanged={() => { loadCompanies(); api.get("/admin/stats").then((r) => setStats(r.data)); }} />
        </TabsContent>

        <TabsContent value="community" className="mt-6">
          <AdminCommunity />
        </TabsContent>

        <TabsContent value="licences" className="mt-6">
          {!companies ? <Spinner /> : companies.length === 0 ? <EmptyState title="No companies yet" /> : (
            <div className="bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden">
              {companies.map((c) => (
                <div key={c.id} className="p-5 border-b border-[#E3E8E8] last:border-0 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-company-${c.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-[#0B1733]">{c.trading_name}</h3>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.status === "approved" ? "bg-[#E8F5F1] text-[#007F83]" : c.status === "rejected" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>{c.status.replace("_", " ")}</span>
                      {c.licence_checked && <LicenceBadge date={c.licence_check_date} />}
                    </div>
                    <p className="text-sm text-[#4A5568] mt-1">{c.legal_name} · Licence {c.licence_number} · {c.issuing_authority} · exp {c.expiry_date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.has_licence ? (
                      <Button size="sm" variant="outline" onClick={() => viewLicence(c.id)} data-testid={`view-licence-${c.slug}`} className="rounded-full"><Eye size={15} className="mr-1" /> Licence</Button>
                    ) : <span className="text-xs text-[#4A5568]">No document</span>}
                    {c.status !== "approved" && <Button size="sm" onClick={() => review(c.id, "approved")} data-testid={`approve-${c.slug}`} className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white"><Check size={15} className="mr-1" /> Approve</Button>}
                    {c.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => review(c.id, "rejected")} data-testid={`reject-${c.slug}`} className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><X size={15} className="mr-1" /> Reject</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-[#4A5568]">Approving confirms the trade licence document is readable and matches stated identity/activities. It does not imply government endorsement. A public "Licence checked" badge appears only after approval.</p>
        </TabsContent>

        <TabsContent value="services" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden">
                {cats.map((c) => (
                  <div key={c.slug} className="p-4 border-b border-[#E3E8E8] last:border-0 flex items-center justify-between" data-testid={`admin-service-${c.slug}`}>
                    <div><p className="font-semibold text-[#0B1733]">{c.name}</p><p className="text-xs text-[#4A5568]">{c.short_description}</p></div>
                    {c.featured && <span className="text-[11px] bg-[#E8F5F1] text-[#007F83] px-2 py-0.5 rounded-full font-semibold">Homepage</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-[#E3E8E8] p-6 h-fit">
              <h3 className="font-display font-bold text-lg text-[#0B1733] mb-4"><Plus size={18} className="inline mr-1" /> Add a service</h3>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={svc.name} onChange={(e) => setSvc({ ...svc, name: e.target.value })} data-testid="svc-name" className="mt-1.5" /></div>
                <div><Label>Short description</Label><Textarea value={svc.short_description} onChange={(e) => setSvc({ ...svc, short_description: e.target.value })} rows={2} data-testid="svc-desc" className="mt-1.5" /></div>
                <div><Label>Parent category</Label><Input value={svc.parent_category} onChange={(e) => setSvc({ ...svc, parent_category: e.target.value })} data-testid="svc-parent" className="mt-1.5" /></div>
                <label className="flex items-center gap-2 text-sm text-[#4A5568]"><input type="checkbox" checked={svc.featured} onChange={(e) => setSvc({ ...svc, featured: e.target.checked })} data-testid="svc-featured" /> Feature on homepage</label>
                <Button onClick={createService} data-testid="svc-create-btn" className="w-full rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Create service</Button>
              </div>
              <p className="mt-3 text-xs text-[#4A5568]">New services appear immediately in the directory, homepage categories and the post-a-job flow — no code changes needed.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="promotions" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {promos.length === 0 ? <EmptyState title="No promotions yet" subtitle="Create a featured placement for an approved company." /> : promos.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5" data-testid={`admin-promo-${p.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-[#0B1733]">{p.company_name}</h3>
                        <span className="text-[11px] bg-[#0B1733]/5 text-[#0B1733]/70 px-2 py-0.5 rounded-full font-semibold capitalize">{p.placement.replace(/_/g, " ")}</span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-[#E8F5F1] text-[#007F83]" : "bg-gray-100 text-gray-600"}`}>{p.status}</span>
                      </div>
                      <p className="text-sm text-[#4A5568] mt-1">{p.start_date} → {p.end_date} · priority {p.priority}{p.free_launch_reason ? ` · ${p.free_launch_reason}` : ""}</p>
                      <p className="text-xs text-[#4A5568] mt-1">Impr {p.metrics.impressions} · Clicks {p.metrics.profile_clicks} · Requests {p.metrics.request_conversions}</p>
                    </div>
                    <div className="flex gap-2">
                      {p.status !== "active" && <Button size="sm" onClick={() => setPromoStatus(p.id, "active")} data-testid={`promo-activate-${p.id}`} className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Activate</Button>}
                      {p.status === "active" && <Button size="sm" variant="outline" onClick={() => setPromoStatus(p.id, "paused")} data-testid={`promo-pause-${p.id}`} className="rounded-full">Pause</Button>}
                      <Button size="sm" variant="outline" onClick={() => setPromoStatus(p.id, "ended")} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">End</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-[#E3E8E8] p-6 h-fit">
              <h3 className="font-display font-bold text-lg text-[#0B1733] mb-4"><Plus size={18} className="inline mr-1" /> Feature a company</h3>
              <div className="space-y-3">
                <div><Label>Company (approved only)</Label>
                  <Select value={promo.company_id} onValueChange={(v) => setPromo({ ...promo, company_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="promo-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>{(companies || []).filter((c) => c.status === "approved" && c.licence_checked).map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Placement</Label>
                  <Select value={promo.placement} onValueChange={(v) => setPromo({ ...promo, placement: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="promo-placement"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homepage_featured">Homepage featured</SelectItem>
                      <SelectItem value="directory_sponsored">Directory sponsored slot</SelectItem>
                      <SelectItem value="search_sponsored">Search sponsored slot</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {promo.placement !== "homepage_featured" && (
                  <div><Label>Target service (optional)</Label>
                    <Select value={promo.category_slug || "none"} onValueChange={(v) => setPromo({ ...promo, category_slug: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1.5" data-testid="promo-category"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Any service</SelectItem>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Start</Label><Input type="date" value={promo.start_date} onChange={(e) => setPromo({ ...promo, start_date: e.target.value })} data-testid="promo-start" className="mt-1.5" /></div>
                  <div><Label>End</Label><Input type="date" value={promo.end_date} onChange={(e) => setPromo({ ...promo, end_date: e.target.value })} data-testid="promo-end" className="mt-1.5" /></div>
                </div>
                <div><Label>Priority (1 = highest)</Label><Input type="number" value={promo.priority} onChange={(e) => setPromo({ ...promo, priority: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Free launch reason (optional)</Label><Input value={promo.free_launch_reason} onChange={(e) => setPromo({ ...promo, free_launch_reason: e.target.value })} placeholder="e.g. Launch pilot grant" data-testid="promo-reason" className="mt-1.5" /></div>
                <Button onClick={createPromo} data-testid="promo-create-btn" className="w-full rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Create promotion</Button>
              </div>
              <p className="mt-3 text-xs text-[#4A5568]">Placements are labelled "Featured placement". Promotion is separate from licence checks and review scores — it never changes organic rankings or reviews.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="disputes" className="mt-6">
          {disputes.length === 0 ? <EmptyState title="No open disputes" subtitle="Reported job issues appear here for resolution with an audit trail." /> : (
            <div className="space-y-4">
              {disputes.map((j) => (
                <div key={j.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5" data-testid={`dispute-${j.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="font-display font-bold text-[#0B1733]">{j.title}</h3>
                      <p className="text-sm text-[#4A5568] mt-1">Customer: {j.owner_name} · Company: {j.awarded_company_name} · {j.service_name}</p>
                      <p className="text-sm text-red-700 mt-2"><b>Reported:</b> {j.dispute_reason}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => resolveDispute(j.id, "completed")} data-testid={`resolve-complete-${j.id}`} className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Mark completed</Button>
                      <Button size="sm" variant="outline" onClick={() => resolveDispute(j.id, "reopen")} data-testid={`resolve-reopen-${j.id}`} className="rounded-full">Reopen</Button>
                      <Button size="sm" variant="outline" onClick={() => resolveDispute(j.id, "cancelled")} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">Cancel job</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
